/**
 * String formatting helpers (medonc dashboard parity).
 */

/**
 * Converts a snake_case string into space-separated capitalized words.
 *
 * @param input - The snake_case string to convert; nullish values yield an empty string
 * @returns The capitalized, space-separated string
 */
export function capitalizeWordsSeperatedByUnderScore(
  input?: string | null,
): string {
  const words = (input ?? "").split("_");
  const capitalizedWords = words
    .filter(Boolean)
    .map(
      (word) =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    );
  return capitalizedWords.join(" ");
}

/**
 * Format API enum strings for display; leaves spaced labels unchanged.
 *
 * @param value - Raw enum from the API
 * @param emptyValue - Placeholder when value is missing
 * @returns Display label
 */
export function formatSnakeCaseEnumLabel(
  value: string | null | undefined,
  emptyValue = "—",
): string {
  if (!value?.trim()) {
    return emptyValue;
  }
  if (!value.includes("_")) {
    return value;
  }
  return capitalizeWordsSeperatedByUnderScore(value);
}

/**
 * Format an eligibility tier using the worklist label convention.
 *
 * @param value - Raw tier value from the API or an existing display label
 * @param emptyValue - Placeholder when the tier is missing
 * @returns Tier label such as `Tier 1 - High`
 */
export function formatEligibilityTierLabel(
  value: string | null | undefined,
  emptyValue = "—",
): string {
  const label = formatSnakeCaseEnumLabel(value, emptyValue);
  if (label === emptyValue || !/^Tier\s+\d+/i.test(label)) {
    return label;
  }
  if (/^Tier\s+\d+\s*[-–—]/i.test(label)) {
    return label;
  }
  return label.replace(/^(Tier\s+\d+)\s+(.+)$/i, "$1 - $2");
}
